// Single source of truth for the notification event registry.
// Mirrored in supabase/functions/_shared/notification-events.ts — KEEP IN SYNC.
export type NotificationCategory =
  | "critical"
  | "opportunities"
  | "active_loads"
  | "network_messages"
  | "updates";

export type NotificationRole = "broker" | "broker_carrier" | "shipper";

export interface NotificationEventDef {
  key: string;
  label: string;
  desc: string;
  category: NotificationCategory;
  roles: NotificationRole[];
  defaultEmail: boolean;
  defaultSms: boolean;
  defaultPush: boolean;
  smsCapable: boolean;
  pushCapable: boolean;
  bypassQuietHours: boolean;
  critical?: boolean;
}

export const NOTIFICATION_EVENTS: NotificationEventDef[] = [
  // ── Critical ────────────────────────────────────────────────────────────
  {
    key: "bid_received",
    label: "Bid received",
    desc: "A broker submitted a quote on your load.",
    category: "critical",
    roles: ["shipper"],
    defaultEmail: true, defaultSms: true, defaultPush: true,
    smsCapable: true, pushCapable: true, bypassQuietHours: true, critical: true,
  },
  {
    key: "load_awarded",
    label: "Load awarded (you won)",
    desc: "Shipper selected your quote.",
    category: "critical",
    roles: ["broker", "broker_carrier"],
    defaultEmail: true, defaultSms: true, defaultPush: true,
    smsCapable: true, pushCapable: true, bypassQuietHours: true, critical: true,
  },
  {
    key: "broker_accepted",
    label: "Broker accepted dispatch",
    desc: "Broker confirmed they will run your load.",
    category: "critical",
    roles: ["shipper"],
    defaultEmail: true, defaultSms: false, defaultPush: true,
    smsCapable: false, pushCapable: true, bypassQuietHours: false, critical: true,
  },
  {
    key: "dispatch_ready",
    label: "Dispatch ready",
    desc: "Shipper marked the dispatch ready to roll.",
    category: "critical",
    roles: ["broker", "broker_carrier"],
    defaultEmail: true, defaultSms: false, defaultPush: true,
    smsCapable: false, pushCapable: true, bypassQuietHours: true, critical: true,
  },
  {
    key: "dispatch_updated",
    label: "Dispatch updated after ready",
    desc: "Shipper changed dispatch details after marking it ready — review before you roll.",
    category: "critical",
    roles: ["broker", "broker_carrier"],
    defaultEmail: true, defaultSms: false, defaultPush: true,
    smsCapable: false, pushCapable: true, bypassQuietHours: true, critical: true,
  },
  {
    key: "dm_received",
    label: "Direct message",
    desc: "Someone sent you a direct message.",
    category: "critical",
    roles: ["broker", "broker_carrier", "shipper"],
    defaultEmail: false, defaultSms: false, defaultPush: true,
    smsCapable: false, pushCapable: true, bypassQuietHours: true, critical: true,
  },

  // ── New opportunities ───────────────────────────────────────────────────
  {
    key: "new_private_load",
    label: "New private network load",
    desc: "A shipper in your private network posted a load.",
    category: "opportunities",
    roles: ["broker", "broker_carrier"],
    defaultEmail: true, defaultSms: true, defaultPush: true,
    smsCapable: true, pushCapable: true, bypassQuietHours: false,
  },
  {
    key: "new_open_load",
    label: "New open marketplace load",
    desc: "A shipper posted a new load to the open LR Direct marketplace.",
    category: "opportunities",
    roles: ["broker", "broker_carrier"],
    defaultEmail: true, defaultSms: true, defaultPush: true,
    smsCapable: true, pushCapable: true, bypassQuietHours: false,
  },
  {
    key: "quote_request_received",
    label: "Quote request received",
    desc: "A shipper invited you to quote a lane.",
    category: "opportunities",
    roles: ["broker", "broker_carrier"],
    defaultEmail: true, defaultSms: true, defaultPush: true,
    smsCapable: true, pushCapable: true, bypassQuietHours: false,
  },

  // ── Active loads ────────────────────────────────────────────────────────
  {
    key: "load_milestone_assigned",
    label: "Carrier assigned",
    desc: "A carrier has been assigned to your load.",
    category: "active_loads",
    roles: ["shipper"],
    defaultEmail: true, defaultSms: false, defaultPush: true,
    smsCapable: false, pushCapable: true, bypassQuietHours: false,
  },
  {
    key: "load_milestone_picked_up",
    label: "Picked up",
    desc: "The driver picked up your load.",
    category: "active_loads",
    roles: ["shipper"],
    defaultEmail: true, defaultSms: false, defaultPush: true,
    smsCapable: false, pushCapable: true, bypassQuietHours: false,
  },
  {
    key: "load_milestone_delivered",
    label: "Delivered",
    desc: "Your load was delivered.",
    category: "active_loads",
    roles: ["shipper"],
    defaultEmail: true, defaultSms: false, defaultPush: true,
    smsCapable: false, pushCapable: true, bypassQuietHours: false,
  },
  {
    key: "pod_uploaded",
    label: "POD uploaded",
    desc: "Proof of delivery is available.",
    category: "active_loads",
    roles: ["shipper"],
    defaultEmail: true, defaultSms: false, defaultPush: true,
    smsCapable: false, pushCapable: true, bypassQuietHours: false,
  },
  {
    key: "dispatch_document_uploaded",
    label: "Dispatch document uploaded",
    desc: "A new document was added to a dispatch.",
    category: "active_loads",
    roles: ["broker", "broker_carrier", "shipper"],
    defaultEmail: true, defaultSms: false, defaultPush: false,
    smsCapable: false, pushCapable: true, bypassQuietHours: false,
  },

  // ── Network & messages ──────────────────────────────────────────────────
  {
    key: "network_invitation",
    label: "Network invitation",
    desc: "A shipper invited you into their private network.",
    category: "network_messages",
    roles: ["broker", "broker_carrier"],
    defaultEmail: true, defaultSms: false, defaultPush: true,
    smsCapable: false, pushCapable: true, bypassQuietHours: false,
  },
  {
    key: "network_join_request",
    label: "Network join request",
    desc: "A broker requested to join your network.",
    category: "network_messages",
    roles: ["shipper"],
    defaultEmail: true, defaultSms: false, defaultPush: true,
    smsCapable: false, pushCapable: true, bypassQuietHours: false,
  },
  {
    key: "quote_response_received",
    label: "Quote response received",
    desc: "A broker responded to your quote request.",
    category: "network_messages",
    roles: ["shipper"],
    defaultEmail: true, defaultSms: true, defaultPush: true,
    smsCapable: true, pushCapable: true, bypassQuietHours: false,
  },

  // ── Updates ─────────────────────────────────────────────────────────────
  {
    key: "bid_not_awarded",
    label: "Bid not awarded",
    desc: "The shipper picked a different broker.",
    category: "updates",
    roles: ["broker", "broker_carrier"],
    defaultEmail: false, defaultSms: false, defaultPush: false,
    smsCapable: false, pushCapable: true, bypassQuietHours: false,
  },
];

export const CATEGORY_LABELS: Record<NotificationCategory, string> = {
  critical: "Critical (always recommended)",
  opportunities: "New opportunities",
  active_loads: "Active loads",
  network_messages: "Network & messages",
  updates: "Updates",
};

export function eventsForRole(role: string | null | undefined): NotificationEventDef[] {
  if (!role) return [];
  return NOTIFICATION_EVENTS.filter((e) => e.roles.includes(role as NotificationRole));
}

export function findEvent(key: string): NotificationEventDef | undefined {
  return NOTIFICATION_EVENTS.find((e) => e.key === key);
}
